/**
 * Single entry point for the router. Each domain contributes a
 * `RouteObject[]` under `/`; this file composes them inside the
 * `ProtectedRoute` + `AppShell` layout.
 *
 * Lazy imports are page-level so Vite emits one chunk per page.
 */

import { lazy, type ReactNode } from 'react';
import {
  createBrowserRouter,
  Navigate,
  useLocation,
  type RouteObject,
} from 'react-router-dom';

import { getUiPref } from '@/config/ui-version';
import { AppShell } from '@/layout/AppShell';
import { ProtectedRoute } from '@/layout/ProtectedRoute';

import { companiesRoutes } from './companies.routes';
import { contractsRoutes } from './contracts.routes';
import { dashboardRoutes } from './dashboard.routes';
import { employeesRoutes } from './employees.routes';
import { homeRoutes } from './home.routes';
import { leaveRoutes } from './leave.routes';
import { meRoutes } from './me.routes';
import { reportsRoutes } from './reports.routes';
import { settingsRoutes } from './settings.routes';
import { vacanciesRoutes } from './vacancies.routes';
import { workersRoutes } from './workers.routes';

const NotFound = lazy(() => import('@/features/not-found/page'));

// The single place v1 touches v2 (eslint carve-out in eslint.config.js).
// Lazy so the v2 chunk never loads in v1-only sessions.
const V2App = lazy(() => import('@/v2/App'));

/**
 * Boot-only UI-preference redirect: on the document-load entry at
 * exactly `/` (react-router marks the initial location with
 * key === 'default'), honor localStorage['relay.ui'] === 'v2' and land
 * on /beta. Pure render logic — StrictMode-proof — and never fires for
 * SPA navigations or explicit URLs, per the dual-run contract.
 */
function BootUiGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  if (location.key === 'default' && getUiPref() === 'v2') {
    return <Navigate to="/beta" replace />;
  }
  return <>{children}</>;
}

const authedChildren: RouteObject[] = [
  ...homeRoutes.map((r) =>
    r.index ? { ...r, element: <BootUiGate>{r.element}</BootUiGate> } : r,
  ),
  ...dashboardRoutes,
  ...workersRoutes,
  ...employeesRoutes,
  ...companiesRoutes,
  ...contractsRoutes,
  ...vacanciesRoutes,
  ...leaveRoutes,
  ...meRoutes,
  ...reportsRoutes,
  ...settingsRoutes,
  { path: '*', element: <NotFound /> },
];

export const router = createBrowserRouter([
  // v2 mini-app — self-contained under /beta, gated inside V2App by
  // the ui_v2 tenant flag (fail-closed). Route ranking makes this win
  // over the v1 catch-all for /beta/*.
  {
    path: 'beta/*',
    element: (
      <ProtectedRoute>
        <V2App />
      </ProtectedRoute>
    ),
  },
  {
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: authedChildren,
  },
]);
